import { useTranslation } from 'react-i18next'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import { Column } from 'primereact/column'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import { formatDate } from '../../../lib/datetime'
import { deleteLabel } from '../../../lib/deleteLabel'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import ActionFollowButton from './ActionFollowButton'
import ActionStatusTag from './ActionStatusTag'
import ImportanceBadge from './ImportanceBadge'
import ActionViewCards from './ActionViewCards'
import { useCanFollowActions } from '../hooks/useActionFollow'
import { mainResponsibleOf, secondaryResponsiblesOf, type MeetingAction, type MinutePerson } from '../types'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import '../actions.css'
import '../../meetings/meetings.css'

interface Props {
  /** Bölüm başlığı (Toplantı Kayıt Tipi'nden — "Aksiyon"). */
  title: string
  actions: MeetingAction[]
  /**
   * **Görevi sil** (2026-09-11) — verilmezse kolon hiç çizilmez. Çağıran bunu yalnız `RECORD_DELETE`
   * yetkisi varken geçer. Bu liste, tutanağı düzenleyemeyen kullanıcıya (ör. Genel Müdür) gösterilir;
   * silme düzenleme yetkisinden bağımsızdır, o yüzden düğme burada da bulunur.
   */
  onDelete?: (action: MeetingAction) => void
}

/**
 * Toplantı Detay'daki Aksiyonlar salt-okuma listesi (analiz 4.7). No · Tanım · Açıklama · Sorumlu ·
 * Termin · Önem Derecesi (rozet — ad sunucuda REFERANS'tan çözülür, renk id'den) · Durum (REFERANS'tan dinamik). Satıra tıklanınca ilgili
 * Aksiyon Detay sayfası açılır (breadcrumb'tan toplantıya geri dönülebilir).
 */
export default function ActionsView({ title, actions, onDelete }: Readonly<Props>) {
  const { t } = useTranslation()
  // 077 — Kayıt No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem); diğerlerinde gizli.
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // Toplantı detayındaki aksiyon satırından detaya giderken bu toplantı origin taşınır → "Geri" toplantıya döner.
  const navigate = useNavigateWithOrigin()
  // Dar ekranda (≤CARD_VIEW_BREAKPOINT) tablo yerine kart listesi — bkz. ActionViewCards.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  // 120 — takip yıldızı: tutanağı okuyan ama görev ekranlarına yetkisi olmayan kullanıcıda çizilmez.
  const canFollow = useCanFollowActions()

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{title}</h2>
      </div>
      {actions.length === 0 ? (
        <EmptyState
          icon="pi pi-check-square"
          title={t('actions.empty.title')}
          description={t('actions.empty.desc')}
        />
      ) : isMobile ? (
        <ActionViewCards
          actions={actions}
          showRecordNumber={showRecordNumber}
          canFollow={canFollow}
          onDelete={onDelete}
        />
      ) : (
        <DataTable
          value={actions}
          dataKey="id"
          paginator={false}
          onRowClick={(e) => navigate(`/minutes/actions/${(e.data as MeetingAction).id}`)}
          rowClassName={() => 'mtg-row-clickable'}
        >
          {showRecordNumber && <Column field="recordNumber" header={t('actions.columns.number')} />}
          <Column
            header={t('actions.columns.name')}
            body={(row: MeetingAction) => (
              <span className="minutes-cell-strong act-cell-clip" title={row.name}>
                {row.name}
              </span>
            )}
          />
          <Column
            header={t('actions.form.description')}
            body={(row: MeetingAction) => (
              <span className="act-cell-clip act-cell-clip--wide" title={row.description ?? ''}>
                {row.description ?? ''}
              </span>
            )}
          />
          <Column
            header={t('actions.columns.mainResponsible')}
            body={(row: MeetingAction) => {
              const name = mainResponsibleOf(row.assignees)?.fullName ?? ''
              return (
                <span className="act-cell-clip" title={name}>
                  {name}
                </span>
              )
            }}
          />
          <Column
            header={t('actions.columns.secondaryResponsibles')}
            body={(row: MeetingAction) => {
              const names = secondaryResponsiblesOf(row.assignees)
                .map((p: MinutePerson) => p.fullName)
                .join(', ')
              return (
                <span className="act-cell-clip" title={names}>
                  {names}
                </span>
              )
            }}
          />
          <Column
            header={t('actions.columns.deadline')}
            body={(row: MeetingAction) => (row.deadline ? formatDate(row.deadline) : '')}
          />
          <Column
            header={t('actions.columns.importance')}
            body={(row: MeetingAction) => (
              <ImportanceBadge levelId={row.importanceLevelId} name={row.importanceLevelName} />
            )}
          />
          <Column
            header={t('actions.columns.status')}
            body={(row: MeetingAction) => (
              <ActionStatusTag statusId={row.statusId} name={row.statusName ?? ''} />
            )}
          />
          {/* Yıldız (120 — görevi gören herkes) + Sil (yalnız `onDelete` verildiğinde, RECORD_DELETE).
              Satır tıklaması Görev Detay'a gider → stopPropagation (aksi hâlde eylem yerine detaya
              giderdi; editördeki düğmelerle aynı kalıp). Kart görünümünde karşılığı `ActionViewCards`'tadır. */}
          {(onDelete || canFollow) && (
            <Column
              header=""
              headerStyle={{ width: '5.5rem' }}
              bodyStyle={{ textAlign: 'center' }}
              body={(row: MeetingAction) => {
                const removeLabel = deleteLabel(t, 'actions.detail.delete.action', row.name)
                return (
                  <span className="act-row-actions">
                    {canFollow && (
                      <ActionFollowButton
                        actionId={row.id}
                        name={row.name}
                        followed={row.followed ?? false}
                      />
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        className="minutes-remove-btn"
                        aria-label={removeLabel}
                        title={removeLabel}
                        onClick={(ev) => {
                          ev.preventDefault()
                          ev.stopPropagation()
                          onDelete(row)
                        }}
                      >
                        <i className="pi pi-trash" aria-hidden="true" />
                      </button>
                    )}
                  </span>
                )
              }}
            />
          )}
        </DataTable>
      )}
    </section>
  )
}
